import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Users,
  Stethoscope,
  FileText,
  Receipt,
  FolderOpen,
  CreditCard,
  Settings,
  Bug,
  LogOut,
  Menu,
  X,
  Globe,
  Activity,
  UserCog
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Appointments from './modules/Appointments';
import Contacts from './modules/Contacts';
import Treatments from './modules/Treatments';
import Quotes from './modules/Quotes';
import Invoices from './modules/Invoices';
import Files from './modules/Files';
import ServiceCards from './modules/ServiceCards';
import WebsiteServices from './modules/WebsiteServices';
import CalendarSettings from './modules/CalendarSettings';
import DebugTools from './modules/DebugTools';
import ImplantList from './modules/ImplantList';
import DoctorManagement from './modules/DoctorManagement';

const Dashboard = () => {
  const [activeModule, setActiveModule] = useState('appointments');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { signOut, user } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const modules = [
    { id: 'appointments', name: 'Appointments', icon: Calendar },
    { id: 'contacts', name: 'Contacts', icon: Users },
    { id: 'treatments', name: 'Treatments', icon: Stethoscope },
    { id: 'quotes', name: 'Quotes', icon: FileText },
    { id: 'invoices', name: 'Invoices', icon: Receipt },
    { id: 'files', name: 'Files', icon: FolderOpen },
    { id: 'implant-list', name: 'Implant List', icon: Activity },
    { id: 'doctor-management', name: 'Doctor Admins', icon: UserCog },
    { id: 'service-cards', name: 'Patient Service Cards', icon: CreditCard },
    { id: 'website-services', name: 'Website Services', icon: Globe },
    { id: 'calendar-settings', name: 'Calendar Settings', icon: Settings },
    { id: 'debug', name: 'Debug Tools', icon: Bug },
  ];

  const renderModule = () => {
    switch (activeModule) {
      case 'appointments': return <Appointments />;
      case 'contacts': return <Contacts />;
      case 'treatments': return <Treatments />;
      case 'quotes': return <Quotes />;
      case 'invoices': return <Invoices />;
      case 'files': return <Files />;
      case 'implant-list': return <ImplantList />;
      case 'doctor-management': return <DoctorManagement />;
      case 'service-cards': return <ServiceCards />;
      case 'website-services': return <WebsiteServices />;
      case 'calendar-settings': return <CalendarSettings />;
      case 'debug': return <DebugTools />;
      default: return <Appointments />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <aside
        className={`bg-white shadow-lg transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-0 md:w-20'
        } flex flex-col fixed md:relative h-screen z-40`}
      >
        <div className="p-4 border-b flex items-center justify-between">
          {sidebarOpen && (
            <h1 className="text-xl font-bold text-blue-600">Admin Panel</h1>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-600 hover:text-gray-800 md:hidden"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-2">
            {modules.map((module) => {
              const Icon = module.icon;
              return (
                <li key={module.id}>
                  <button
                    onClick={() => setActiveModule(module.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      activeModule === module.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={20} />
                    {sidebarOpen && <span>{module.name}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut size={20} />
            {sidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden text-gray-600 hover:text-gray-800"
            >
              <Menu size={24} />
            </button>
            <div className="text-sm text-gray-600">
              Logged in as: {user?.email}
            </div>
          </div>
          {renderModule()}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
